import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { apiFetch, getToken, clearToken, isTokenExpired, DPP_BASE_URL } from '../dppApi';
import AccountMenu from './AccountMenu';
import DeleteAccountPanel from './DeleteAccountPanel';
import EstatePlanner from './EstatePlanner';
import '../App.css';

const bloomMark = process.env.PUBLIC_URL + '/BloomFavicon.png';

// The account page (route /user/profile): a left settings nav + body. Three
// sections — Profile (read-only name/email), Estate planning, and Delete account.
// The Estate planning editor lives in the shared <EstatePlanner> so the owner and
// the lawyer's share link (/estate/:token) render the identical screen. Lands on
// Profile by default.
const ProfilePage = () => {
    const navigate = useNavigate();
    const token = getToken('user');
    const [params] = useSearchParams();

    // Lands on Profile, unless routed straight to Delete (e.g. after signing in
    // from the public /delete-account page: /user/profile?section=delete).
    const [nav, setNav] = useState(params.get('section') === 'delete' ? 'delete' : 'profile');
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // An expired token still passes a bare `!token` check — mirror UserCollection.
        if (!token || isTokenExpired(token)) { clearToken('user'); navigate('/user'); return; }
        apiFetch('/api/user/me', { token })
            .then(setMe)
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [token, navigate]);

    const logout = () => { clearToken('user'); navigate('/user'); };

    // The owner's estate endpoints (own JWT), handed to the shared editor.
    const ownerApi = {
        get: () => apiFetch('/api/user/estate', { token }),
        saveDesignation: (body) => apiFetch('/api/user/estate/designation', { method: 'PUT', token, body }),
        clearDesignation: (pid) => apiFetch(`/api/user/estate/designation/${pid}`, { method: 'DELETE', token }),
        saveExecutor: (body) => apiFetch('/api/user/estate/executor', { method: 'PUT', token, body }),
        uploadWill: (form) => apiFetch('/api/user/estate/will', { method: 'POST', token, body: form, isForm: true }),
        createShare: () => apiFetch('/api/user/estate/share', { method: 'POST', token }),
        revokeShare: () => apiFetch('/api/user/estate/share', { method: 'DELETE', token }),
        createExecutorShare: () => apiFetch('/api/user/estate/executor-share', { method: 'POST', token }),
        revokeExecutorShare: () => apiFetch('/api/user/estate/executor-share', { method: 'DELETE', token }),
    };

    return (
        <div className="dpp-page">
            <div className="collection-topbar">
                <div className="collection-topbar-brand">
                    <img className="dpp-brand-mark" src={bloomMark} alt="Bloom" />
                    <h1 className="collection-topbar-title">My Account</h1>
                </div>
                <AccountMenu
                    name={me?.name}
                    onLogout={logout}
                    onSettings={() => navigate('/user/profile')}
                />
            </div>
            <div className="account-back">
                <Link to="/user/collection" className="legal-home">← Back to Collection</Link>
            </div>

            <div className="settings-wrap dpp-card account-settings">
                <nav className="settings-nav">
                    <button className={`snav-item${nav === 'profile' ? ' is-active' : ''}`} onClick={() => setNav('profile')}>
                        Profile
                    </button>
                    <button className={`snav-item${nav === 'estate' ? ' is-active' : ''}`} onClick={() => setNav('estate')}>
                        Estate planning
                    </button>
                    <button className={`snav-item snav-item--danger${nav === 'delete' ? ' is-active' : ''}`} onClick={() => setNav('delete')}>
                        Delete account
                    </button>
                </nav>

                <div className="settings-body">
                    {nav === 'profile' && (
                        <div className="settings-section">
                            <h2 className="settings-section-title">Profile</h2>
                            <p className="settings-section-sub">Your account details.</p>
                            {loading && <p className="collection-status collection-status--flush">Loading…</p>}
                            {error && <p className="dpp-error">{error}</p>}
                            <div className="estate-rows">
                                <div className="estate-row">
                                    <span className="estate-row-label">Name</span>
                                    <span className="estate-row-value">{me?.name || '—'}</span>
                                </div>
                                <div className="estate-row">
                                    <span className="estate-row-label">Email</span>
                                    <span className="estate-row-value">{me?.email || '—'}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {nav === 'estate' && (
                        <EstatePlanner
                            api={ownerApi}
                            showShare
                            passportHref={p => `${DPP_BASE_URL}/jewellery/${p.qr_code_id}?page=4`} />
                    )}

                    {nav === 'delete' && (
                        <div className="settings-section dpp-narrow-page settings-delete">
                            <DeleteAccountPanel
                                onCancel={() => setNav('profile')}
                                onDeleted={(result) => navigate('/delete-account', { state: { result } })} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
